import { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { LayoutDashboard, Package, FolderTree, Boxes, ShoppingBag, Store, Users, Ticket, RotateCcw, ScrollText, BarChart3, LogOut, Zap, Menu, X, ShieldCheck, Smartphone, Building2, Handshake, Truck, ArrowLeftRight, TrendingUp, Undo2, PackagePlus, CalendarClock, Gift, LineChart, Wallet, ReceiptText, Tags, Plus, BookOpen, KeyRound, Wrench, UserCog, Warehouse, Star, Images, Megaphone, Briefcase, Contact, CreditCard, SlidersHorizontal } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/context/RealtimeContext";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BusinessSwitcher } from "@/context/BusinessContext";
import { SyncHealthBadge } from "@/components/SyncHealthBadge";

const STAFF = ["super_admin", "owner", "store_manager", "staff"];
const NAV = [
  ["/admin", LayoutDashboard, "Dashboard", true],
  ["/admin/analytics", TrendingUp, "Sales Analytics"],
  ["/admin/profit", LineChart, "Profit Margins"],
  ["/admin/loyalty", Gift, "Loyalty Rewards"],
  ["/admin/sync", Smartphone, "App & POS Sync"],
  ["/admin/api-analytics", KeyRound, "API Analytics"],
  ["/admin/media", Images, "Media Library"],
  ["/admin/banners", Megaphone, "Homepage Banners"],
  ["/admin/products", Package, "Products"],
  ["/admin/categories", FolderTree, "Categories"],
  ["/admin/inventory", Boxes, "Inventory"],
  ["/admin/reorder", PackagePlus, "Auto Reorder"],
  ["/admin/batches", CalendarClock, "Expiry & Batches"],
  ["/admin/stores", Building2, "Stores"],
  ["/admin/warehouses", Warehouse, "Godowns"],
  ["/admin/transfers", ArrowLeftRight, "Branch Transfer"],
  ["/admin/suppliers", Truck, "Suppliers & Purchases"],
  ["/admin/purchase-returns", Undo2, "Purchase Returns"],
  ["/admin/sellers", Handshake, "Sellers"],
  ["/admin/businesses", Briefcase, "Businesses", false, ["super_admin", "owner", "store_manager"]],
  ["/admin/agents", Contact, "Commission Agents", false, ["super_admin", "owner", "store_manager"]],
  ["/admin/payments", CreditCard, "Payment & UPI", false, ["super_admin", "owner", "store_manager"]],
  ["/admin/product-defaults", SlidersHorizontal, "Product Defaults", false, ["super_admin", "owner", "store_manager"]],
  ["/admin/orders", ShoppingBag, "Orders"],
  ["/admin/service-jobs", Wrench, "Service Jobs", false, [...STAFF, "technician"]],
  ["/admin/pos", Store, "Billing Counter", false, [...STAFF, "salesman"]],
  ["/admin/my-sales", ReceiptText, "My Sales", false, [...STAFF, "salesman"]],
  ["/admin/khata", Wallet, "Khata / Udhaar"],
  ["/admin/cashbook", BookOpen, "Cashbook"],
  ["/admin/business", ReceiptText, "Business Settings"],
  ["/admin/labels", Tags, "Barcode Labels"],
  ["/admin/customers", Users, "Customers"],
  ["/admin/team", UserCog, "Team & Roles", false, ["super_admin", "owner"]],
  ["/admin/coupons", Ticket, "Offers & Coupons"],
  ["/admin/reviews", Star, "Reviews & Ratings"],
  ["/admin/brands", Tags, "Brands"],
  ["/admin/delivery", Truck, "Delivery & Shipping"],
  ["/admin/warranties", ShieldCheck, "Warranties"],
  ["/admin/amc", Wrench, "AMC Contracts"],
  ["/admin/quotations", ReceiptText, "Quotations"],
  ["/admin/bookings", CalendarClock, "Advance Bookings"],
  ["/admin/returns", RotateCcw, "Returns & Refunds"],
  ["/admin/reports", BarChart3, "Reports & GST"],
  ["/admin/audit", ScrollText, "Audit Logs"],
];

const PANEL = [...STAFF, "salesman", "technician"];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { connected, subscribe } = useRealtime();
  const [open, setOpen] = useState(false);
  const [firm, setFirm] = useState("Rs. Mart");
  const [custOpen, setCustOpen] = useState(false);
  const [cust, setCust] = useState({ name: "", phone: "" });

  useEffect(() => { api.get("/admin/business-settings").then((r) => {
    setFirm(r.data.name || "Rs. Mart");
    const dl = r.data.trial_days_left;
    if (r.data.is_premium && typeof r.data.premium_days_left === "number" && r.data.premium_days_left <= 3)
      toast(`⭐ Premium sirf ${r.data.premium_days_left} din me khatam — Renew karein`, { duration: 7000 });
    else if (r.data.trial_expired) toast.error("Free trial khatam — Premium lein");
    else if (typeof dl === "number" && dl <= 2 && dl > 0) toast(`⏳ Trial: sirf ${dl} din baaki — Premium le lein`, { duration: 6000 });
  }).catch(() => {}); }, []);

  const saveCustomer = async () => {
    try {
      await api.post("/pos/customers", cust);
      toast.success("Customer saved");
      const c = { ...cust };
      setCustOpen(false); setCust({ name: "", phone: "" });
      nav(`/admin/pos?phone=${encodeURIComponent(c.phone)}&name=${encodeURIComponent(c.name || "")}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save customer"); }
  };

  const [purchOpen, setPurchOpen] = useState(false);
  const [sups, setSups] = useState([]);
  const [prods, setProds] = useState([]);
  const [strs, setStrs] = useState([]);
  const [po, setPo] = useState({ supplier_id: "", store_id: "store_main", invoice_no: "", items: [] });
  const [pline, setPline] = useState({ product_id: "", quantity: 1, cost_price: 0 });

  const openPurchase = () => {
    setPurchOpen(true);
    if (!sups.length) api.get("/admin/suppliers").then((r) => setSups(r.data)).catch(() => {});
    if (!prods.length) api.get("/products?limit=1000").then((r) => setProds(r.data)).catch(() => {});
    if (!strs.length) api.get("/stores").then((r) => setStrs(r.data)).catch(() => {});
  };
  const addPline = () => {
    if (!pline.product_id || pline.quantity < 1) return toast.error("Product & qty chuno");
    const p = prods.find((x) => x.id === pline.product_id);
    setPo((s) => ({ ...s, items: [...s.items, { ...pline, name: p?.name }] }));
    setPline({ product_id: "", quantity: 1, cost_price: 0 });
  };
  const savePurchase = async () => {
    if (!po.supplier_id || !po.items.length) return toast.error("Supplier chuno & item add karo");
    try {
      const { data } = await api.post("/admin/purchases", { supplier_id: po.supplier_id, store_id: po.store_id, invoice_no: po.invoice_no,
        items: po.items.map((i) => ({ product_id: i.product_id, quantity: parseInt(i.quantity), cost_price: parseFloat(i.cost_price) })) });
      toast.success("Purchase received — stock updated"); setPurchOpen(false); setPo({ supplier_id: "", store_id: "store_main", invoice_no: "", items: [] });
      if (data?.id) window.open(`/print/purchase/${data.id}`, "_blank");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save purchase"); }
  };

  useEffect(() => {
    if (user === false) nav("/login?next=/admin");
    else if (user && !PANEL.includes(user.role)) nav("/");
    else if (user && window.location.pathname === "/admin") {
      if (user.role === "technician") nav("/admin/service-jobs");
      else if (user.role === "salesman") nav("/admin/my-sales");
    }
  }, [user]);
  useEffect(() => subscribe((m) => {
    if (m.event === "order.created") toast.success(`New ${m.payload.type} order ${m.payload.order_no} · ₹${m.payload.total}`);
  }), [subscribe]);

  if (!user || !PANEL.includes(user.role)) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;

  return (
    <div className="flex h-screen bg-slate-100 overflow-hidden">
      <aside className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:static z-50 w-64 h-full bg-slate-900 text-slate-400 flex flex-col shrink-0 transition-transform`}>
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center gap-2"><div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center text-white font-extrabold">₹</div><div><p className="text-white font-bold leading-none" data-testid="sidebar-firm-name">{firm}</p><p className="text-[10px] text-emerald-400">Rs. MART POS · ERP</p></div></div>
          <button className="lg:hidden text-slate-400" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-1">
          {NAV.filter(([, , , , roles]) => (roles || STAFF).includes(user.role)).map(([to, Icon, label, end]) => (
            <NavLink key={to} to={to} end={end} data-testid={`nav-${label.toLowerCase().replace(/[^a-z]/g, "-")}`} onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-emerald-600 text-white" : "hover:bg-slate-800 hover:text-white"}`}>
              <Icon size={18} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-2 px-3 py-2 text-xs"><ShieldCheck size={14} className="text-emerald-400" /><span className="truncate">{user.email}</span></div>
          <p className="px-3 text-[10px] uppercase tracking-wider text-emerald-500 font-bold">{user.role.replace("_", " ")}</p>
          <button data-testid="admin-logout" onClick={async () => { await logout(); nav("/"); }} className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-slate-800 hover:text-white transition-colors"><LogOut size={16} /> Logout</button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-slate-200 px-3 lg:px-6 py-3 flex items-center justify-between gap-2 shrink-0">
          <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-x-auto no-scrollbar">
            <button className="lg:hidden shrink-0" onClick={() => setOpen(true)} data-testid="admin-menu-btn"><Menu size={22} /></button>
            {STAFF.includes(user.role) && <>
            <button data-testid="quick-new-bill" onClick={() => nav("/admin/pos")} className="shrink-0 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700 inline-flex items-center gap-1"><Plus size={13} /> New Bill</button>
            <button data-testid="quick-add-customer" onClick={() => setCustOpen(true)} className="shrink-0 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs font-bold hover:bg-blue-100 inline-flex items-center gap-1"><Plus size={13} /> Customer</button>
            <button data-testid="quick-purchase-entry" onClick={openPurchase} className="shrink-0 px-3 py-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-xs font-bold hover:bg-purple-100 inline-flex items-center gap-1"><Plus size={13} /> Purchase Entry</button>
            </>}
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold shrink-0">
            <BusinessSwitcher />
            <SyncHealthBadge />
            <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${connected ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="rt-indicator">
              <Zap size={11} className={connected ? "fill-emerald-600" : ""} /> <span className="hidden sm:inline">{connected ? "Live sync on" : "Reconnecting…"}</span>
            </span>
            <button onClick={() => nav("/")} className="hidden sm:inline-block px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50">View Store</button>
          </div>
        </header>

        {/* Quick add customer */}
        <Dialog open={custOpen} onOpenChange={setCustOpen}>
          <DialogContent className="bg-white max-w-sm" data-testid="quick-customer-modal">
            <DialogHeader><DialogTitle>Add Customer</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <input data-testid="qc-name" placeholder="Customer name" value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="qc-phone" placeholder="Phone (WhatsApp)" value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" />
              <button data-testid="qc-save" onClick={saveCustomer} className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold">Save Customer</button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Quick purchase entry */}
        <Dialog open={purchOpen} onOpenChange={setPurchOpen}>
          <DialogContent className="bg-white max-w-lg" data-testid="quick-purchase-modal">
            <DialogHeader><DialogTitle>Purchase Entry</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-1">
              <div className="grid grid-cols-2 gap-2">
                <select data-testid="qp-supplier" value={po.supplier_id} onChange={(e) => setPo({ ...po, supplier_id: e.target.value })} className="border border-slate-300 rounded-lg px-2 py-2 text-sm">
                  <option value="">Select supplier…</option>
                  {sups.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select data-testid="qp-store" value={po.store_id} onChange={(e) => setPo({ ...po, store_id: e.target.value })} className="border border-slate-300 rounded-lg px-2 py-2 text-sm">
                  {strs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <input data-testid="qp-invoice" placeholder="Supplier invoice no (optional)" value={po.invoice_no} onChange={(e) => setPo({ ...po, invoice_no: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              <div className="flex gap-2 items-end">
                <select data-testid="qp-product" value={pline.product_id} onChange={(e) => setPline({ ...pline, product_id: e.target.value })} className="flex-1 border border-slate-300 rounded-lg px-2 py-2 text-sm">
                  <option value="">Product…</option>
                  {prods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <input data-testid="qp-qty" type="number" min="1" value={pline.quantity} onChange={(e) => setPline({ ...pline, quantity: e.target.value })} placeholder="Qty" className="w-16 border border-slate-300 rounded-lg px-2 py-2 text-sm" />
                <input data-testid="qp-cost" type="number" min="0" value={pline.cost_price} onChange={(e) => setPline({ ...pline, cost_price: e.target.value })} placeholder="Cost" className="w-20 border border-slate-300 rounded-lg px-2 py-2 text-sm" />
                <button data-testid="qp-addline" onClick={addPline} className="px-3 py-2 bg-slate-800 text-white rounded-lg text-sm font-bold">Add</button>
              </div>
              {po.items.length > 0 && (
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-50 max-h-40 overflow-y-auto text-sm">
                  {po.items.map((i, x) => (
                    <div key={`${i.product_id}-${x}`} className="flex justify-between px-3 py-1.5"><span className="line-clamp-1">{i.name}</span><span className="text-slate-500">{i.quantity} × ₹{i.cost_price}</span></div>
                  ))}
                </div>
              )}
              <button data-testid="qp-save" onClick={savePurchase} className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold">Receive Purchase (stock +)</button>
            </div>
          </DialogContent>
        </Dialog>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6"><Outlet /></main>
      </div>
    </div>
  );
}
